import { useState, useRef } from 'react';
import { T, useTheme, dens, Card } from '../../../design';
import { fmtCount } from '../../../utils';
import { statMonthLabel } from '../primeStatsConfig';
import type { DevelopmentPoint } from '../../../types/primeStats.types';

function DevelopmentChart({ data }: { data: DevelopmentPoint[] }) {
  const t = useTheme();
  const n = data.length;
  const [sel, setSel] = useState<number | null>(null); // null => live-punkt
  const wrapRef = useRef<HTMLDivElement>(null);
  const active = sel == null ? n - 1 : sel;

  const W = 344, H = 168, padL = 6, padR = 6, padT = 16;
  const innerW = W - padL - padR, innerH = H - padT - 26;
  const vals = data.map((p) => p.total);
  const max = Math.max(...vals), min = Math.min(...vals);
  const lo = Math.max(0, min - (max - min) * 0.35 - 1);
  const hi = max + (max - min) * 0.18 + 1;
  const X = (i: number) => padL + innerW * (n === 1 ? 0.5 : i / (n - 1));
  const Y = (v: number) => padT + innerH * (1 - (v - lo) / (hi - lo || 1));

  const linePts = data.map((p, i) => [X(i), Y(p.total)] as [number, number]);
  const solid = linePts.slice(0, n - 1).map((pt, i) => (i === 0 ? 'M' : 'L') + pt[0].toFixed(1) + ' ' + pt[1].toFixed(1)).join(' ');
  const liveSeg = n >= 2
    ? 'M' + linePts[n - 2][0].toFixed(1) + ' ' + linePts[n - 2][1].toFixed(1) + ' L' + linePts[n - 1][0].toFixed(1) + ' ' + linePts[n - 1][1].toFixed(1)
    : '';

  const grid = [0, 0.5, 1].map((g) => padT + innerH * g);
  const tickIdx = [0, 6, 12, 18, 24].filter((i) => i < n);

  function pick(e: React.PointerEvent<HTMLDivElement>) {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    setSel(Math.round(frac * (n - 1)));
  }

  const ap = data[active];
  const ax = X(active), ay = Y(ap.total);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontSize: 12, color: T.fg3, fontWeight: 600 }}>
          {statMonthLabel(ap, true)}{ap.isLive ? ' · nu' : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 20, fontWeight: 800, color: T.fg, letterSpacing: '-0.02em' }}>{fmtCount(ap.total)}</span>
          <span style={{ fontSize: 11, color: T.fg3, fontWeight: 600 }}>medl.</span>
        </div>
      </div>
      <div ref={wrapRef} style={{ position: 'relative', touchAction: 'pan-y', cursor: 'pointer' }}
        onPointerDown={pick} onPointerMove={(e) => { if (e.buttons) pick(e); }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', userSelect: 'none' }} role="img" aria-label="Udviklingsgraf">
          {grid.map((gy, i) => <line key={i} x1={padL} y1={gy} x2={W - padR} y2={gy} stroke={T.borderLight} strokeWidth="1" />)}
          <line x1={ax} y1={padT - 4} x2={ax} y2={padT + innerH} stroke={t.accent} strokeWidth="1" strokeDasharray="2 3" opacity="0.4" />
          <path d={solid} fill="none" stroke={t.accent} strokeWidth="2.5" />
          {liveSeg && <path d={liveSeg} fill="none" stroke={t.accent} strokeWidth="2.5" strokeDasharray="4 3" opacity="0.7" />}
          <circle cx={X(n - 1)} cy={Y(data[n - 1].total)} r="4.5" fill={T.surface} stroke={t.accent} strokeWidth="2.5" />
          {active !== n - 1 && <circle cx={ax} cy={ay} r="4" fill={t.accent} stroke={T.surface} strokeWidth="2" />}
          {tickIdx.map((i) => (
            <text key={i} x={X(i)} y={H - 8} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
              fontSize="10.5" fontWeight="600" fill={T.fg3} fontFamily={T.font}>
              {statMonthLabel(data[i], true)}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}

export function ChartCard({ title, data, note }: { title: string; data: DevelopmentPoint[]; note?: string }) {
  const t = useTheme();
  const d = dens(t);
  return (
    <Card pad={d.pad}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.fg }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: T.fg3, fontWeight: 600 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, border: `2px solid ${t.accent}`, background: T.surface }} />
          indeværende md.
        </div>
      </div>
      <DevelopmentChart data={data} />
      {note && <div style={{ fontSize: 11, color: T.fg3, marginTop: 8 }}>{note}</div>}
    </Card>
  );
}
